<template>
  <div class="col-wrapper" :style="getWrapperHeight">
    <ul class="wheel-list" :style="getListTop" ref="wheel">
      <li
        class="wheel-item"
        v-for="(item, i) in values"
        :key="i"
        :style="getWheelItemStyle(i)"
        :class="{ 'selected': isSelected(i) }"
      >
        {{item.label}}
      </li>
    </ul>
    <div class="cover" :style="getCoverStyle"></div>
    <div class="divider" :style="getDividerStyle"></div>
  </div>
</template>

<script>
import Animate from '../../utils/animate'

const radius = 100 // 半径
const lineHeight = 36 // 文字行高
const totalHeight = 2 * radius // 容器总高度
const visibleItems = 5 // 可见的项目数量
const selectedIndex = Math.floor(visibleItems / 2) // 中间位置为选中项

export default {
  props: {
    values: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      currentPosition: 0, // 当前滚动位置（以px为单位）
      selectedPosition: 0, // 选中项的位置
      finger: {
        startY: 0,
        endY: 0,
        startTime: 0,
        endTime: 0,
        currentMove: 0,
        prevMove: 0
      },
      isAnimating: false
    }
  },
  computed: {
    getListTop () {
      return {
        top: `${radius - Math.round(lineHeight / 2)}px`,
        height: `${lineHeight}px`
      }
    },
    getWrapperHeight () {
      return {
        height: `${totalHeight}px`
      }
    },
    getCoverStyle () {
      return {
        backgroundSize: `100% ${radius - Math.round(lineHeight / 2)}px`
      }
    },
    getDividerStyle () {
      return {
        top: `${radius - Math.round(lineHeight / 2)}px`,
        height: `${lineHeight - 2}px`
      }
    },
    animate () {
      return new Animate()
    },
    // 计算当前选中的索引
    currentSelectedIndex () {
      const index = Math.round(this.currentPosition / lineHeight)
      return Math.max(0, Math.min(index, this.values.length - 1))
    },
    // 计算可见区域偏移
    listOffset () {
      return this.currentPosition
    }
  },
  mounted () {
    this.$el.addEventListener('touchstart', this.listenerTouchStart, false)
    this.$el.addEventListener('touchmove', this.listenerTouchMove, false)
    this.$el.addEventListener('touchend', this.listenerTouchEnd, false)

    // 初始化选中第一个
    this.$emit('select', this.values[0])
  },
  beforeDestroy () {
    this.$el.removeEventListener('touchstart', this.listenerTouchStart, false)
    this.$el.removeEventListener('touchmove', this.listenerTouchMove, false)
    this.$el.removeEventListener('touchend', this.listenerTouchEnd, false)
  },
  methods: {
    getWheelItemStyle (index) {
      // 线性布局，没有3D旋转
      return {
        transform: `translateY(${-this.currentPosition + (index * lineHeight)}px)`,
        height: `${lineHeight}px`,
        lineHeight: `${lineHeight}px`
      }
    },

    isSelected (index) {
      return index === this.currentSelectedIndex
    },

    listenerTouchStart (ev) {
      ev.stopPropagation()
      ev.preventDefault()
      this.isAnimating = false
      this.finger.startY = ev.targetTouches[0].pageY
      this.finger.prevMove = this.currentPosition
      this.finger.startTime = Date.now()
    },

    listenerTouchMove (ev) {
      ev.stopPropagation()
      ev.preventDefault()

      const deltaY = this.finger.startY - ev.targetTouches[0].pageY
      const newPosition = this.finger.prevMove + deltaY

      // 限制滚动范围
      this.currentPosition = this.clampPosition(newPosition)
      this.finger.currentMove = this.currentPosition
    },

    listenerTouchEnd (ev) {
      ev.stopPropagation()
      ev.preventDefault()

      this.finger.endY = ev.changedTouches[0].pageY
      this.finger.endTime = Date.now()

      // 计算滑动速度
      const deltaY = this.finger.startY - this.finger.endY
      // const deltaY = 5
      const deltaTime = this.finger.endTime - this.finger.startTime
      const velocity = deltaTime > 0 ? deltaY / deltaTime : 0

      this.performMomentumScroll(velocity)
    },

    // 限制位置在有效范围内
    clampPosition (position) {
      const minPosition = 0
      const maxPosition = Math.max(0, (this.values.length - 1) * lineHeight)
      return Math.max(minPosition, Math.min(position, maxPosition))
    },

    // 执行惯性滚动
    performMomentumScroll (velocity) {
      const momentumDuration = 300 // 惯性滚动持续时间
      const momentumDistance = velocity * momentumDuration / 2

      let targetPosition = this.currentPosition + momentumDistance

      // 确保停止在项目边界上
      targetPosition = Math.round(targetPosition / lineHeight) * lineHeight

      // 限制在有效范围内
      targetPosition = this.clampPosition(targetPosition)

      this.smoothScrollTo(targetPosition, momentumDuration)
    },

    // 平滑滚动到指定位置
    smoothScrollTo (targetPosition, duration = 300) {
      this.isAnimating = true

      const startPosition = this.currentPosition
      const startTime = Date.now()

      const animate = () => {
        if (!this.isAnimating) return

        const currentTime = Date.now()
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)

        // 使用缓动函数
        const easedProgress = this.easeOutCubic(progress)

        this.currentPosition = startPosition + (targetPosition - startPosition) * easedProgress

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.currentPosition = targetPosition
          this.isAnimating = false
          this.onScrollEnd()
        }
      }

      requestAnimationFrame(animate)
    },

    // 缓动函数
    easeOutCubic (t) {
      return 1 - Math.pow(1 - t, 3)
    },

    // 滚动结束回调
    onScrollEnd () {
      // 确保停在精确的项目位置
      const nearestIndex = Math.round(this.currentPosition / lineHeight)
      this.currentPosition = Math.max(0, Math.min(nearestIndex, this.values.length - 1)) * lineHeight

      // 触发选择事件
      const selectedIndex = Math.round(this.currentPosition / lineHeight)
      if (selectedIndex >= 0 && selectedIndex < this.values.length) {
        this.$emit('select', this.values[selectedIndex])
      }
    },

    // 手动选择项目
    selectItem (index) {
      if (index >= 0 && index < this.values.length) {
        const targetPosition = index * lineHeight
        this.smoothScrollTo(targetPosition)
        this.$emit('select', this.values[index])
      }
    }
  }
}
</script>

<style lang="less">
  ul, li {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .col-wrapper {
    position: relative;
    border: 1px solid #CCC;
    height: 300px;
    overflow: hidden;
    text-align: center;
    color: #000;

    .wheel-list {
      position: absolute;
      width: 100%;
      transition: transform 0.3s ease-out;

      .wheel-item {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;

        &.selected {
          color: #007AFF;
          font-weight: bold;
          transform: scale(1.1);
        }
      }
    }

    .cover {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      background-image:
        linear-gradient(180deg, hsla(0,0%,100%,0.95), hsla(0,0%,100%,0.6)),
        linear-gradient(0deg, hsla(0,0%,100%,0.95), hsla(0,0%,100%,0.6));
      background-position: top, bottom;
      background-repeat: no-repeat;
      pointer-events: none;
    }

    .divider {
      position: absolute;
      width: 100%;
      left: 0;
      border-top: 1px solid #cccccc;
      border-bottom: 1px solid #cccccc;
      pointer-events: none;
    }
  }
</style>
